import { Component, OnInit, OnChanges, Input, SimpleChanges, Output, EventEmitter, OnDestroy } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators, UntypedFormArray, UntypedFormControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { Store } from '@ngrx/store';
import { OpCodeState } from './../../../store/opcode/opcode.reducer';
import * as fromOpCodeActions from './../../../store/opcode/opcode.actions';
import * as fromOpCodeSelectors from './../../../store/opcode/opcode.selectors';
import { Observable } from 'rxjs';
import { first } from 'rxjs/operators';
import { IServiceOpcode, PricingType, OpcodeType } from '@signal/asp-data-commons';
import { AlertService } from 'src/app/modules/shared/services/alert.service';
import {TranslateService} from '@ngx-translate/core';
import { DateAdapter } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from 'src/app/modules/shared/components/confirm-dialog/confirm-dialog.component';
import { globalTXM, countryCd, unsubscribeSubscriptions } from '../../../../modules/shared/services/util.service';
import { SubscriptionList } from '../../../shared/models/asp.types';

@Component({
  selector: 'app-opcode-service-form',
  templateUrl: './opcode-service-form.component.html',
  styleUrls: ['./opcode-service-form.component.scss']
})
export class OpcodeServiceFormComponent implements OnInit, OnChanges, OnDestroy {
  @Input('opCode') opCode: string;
  @Input('isAllOpCodeList') isAllOpCodeList: boolean;
  @Output() formEdited:EventEmitter<boolean>=new EventEmitter();
  @Input() saveClicked:EventEmitter<boolean>=new EventEmitter();
  opCodeForm: UntypedFormGroup;
  isLoading$: Observable<boolean>;
  opCode$: Observable<IServiceOpcode>;
  mileages$: Observable<number[]>;
  readonly pricingType = PricingType;
  disableForm: boolean;
  allOpcodes: IServiceOpcode[];
  filteredOptions: IServiceOpcode[];
  selectedOpcode: string = "";
  miscOpcode$: Observable<string[]>;
  prevDesc: string = '';
  miscDefaultDescription = 'Not finding what you are looking for? Please add the reason for your service.';
  isCheckOpcodesLoading$: Observable<boolean>;
  isOpcodeExists$: Observable<boolean>;
  isEnglishText:boolean = true;
  isAspComplaintCode:boolean = false;
  toShowTXMfield: boolean = false;
  isToyotabrand: boolean = true;
  onlineOpcodesCount: number = 0;
  isUsDealer : boolean = countryCd.toUpperCase().includes('US');
  isMEXDealer: boolean = countryCd.toUpperCase().includes('MEX');
  pricingTypeKey : string = 'pricing.pricingType';
  pricingShowKey : string = 'pricing.show';
  pricingFixedKey : string = 'pricing.fixed';
  pricingStartsAtKey : string = 'pricing.startsAt';
  pricingRangeFrom : string = 'pricing.range.from';
  pricingRangeTo : string = 'pricing.range.to';
  discountPrice : string = 'pricing.discount.price';
  private subs: SubscriptionList = {};
  validatedFields = ['displayName'];
  addonSubCode = new UntypedFormControl('');
  allDmsOpcodes= [];
  allDmsOpcodesSubOptions = [];

  get milageValue() {
    let mileage = this.opCodeForm.get('mileages').value;
    mileage = (Array.isArray(mileage) && mileage.length) ? mileage[0] : '';
    return mileage;
  }

  constructor(private readonly formBuilder: UntypedFormBuilder,
    private readonly opCodeStore: Store<OpCodeState>,
    private readonly alertService: AlertService,
    private readonly translate : TranslateService,
    private dateAdapter: DateAdapter<Date>,
    public dialog: MatDialog
    ) {
    this.dateAdapter.setLocale(this.translate.currentLang);
    if(this.translate.currentLang?.includes('es')){
      this.isEnglishText = false;
    }
    this.toShowTXMfield = globalTXM;
    }

  ngOnInit(): void {
    this.disableForm = true;
    this.clearForm();

    // Load mileages master data
    this.loadMileage();

    this.subs.allOpcodes = this.opCodeStore.select(fromOpCodeSelectors.selectAllOpCodes).subscribe(opcodes=> {
      this.allOpcodes = opcodes;
    });

    this.subs.dmsOpcodes = this.opCodeStore.select(fromOpCodeSelectors.selectDmsOpCodes).subscribe(opcodes=> {
      this.filteredOptions = opcodes.slice();
      this.allDmsOpcodes = opcodes.slice();
    });
    this.miscOpcode$ = this.opCodeStore.select(fromOpCodeSelectors.miscOpcode);
    this.subs.onlineCount = this.opCodeStore.select(fromOpCodeSelectors.selectOnlineOpCodeCount).subscribe(count=>{
      this.onlineOpcodesCount = count;
    })
  }

  ngOnChanges(changes: SimpleChanges) {
    const opCode = changes.opCode?.currentValue;
    if (!!opCode) {
      this.disableForm = false;
      this.filteredOptions = this.allDmsOpcodes.slice();
      setTimeout(() => this.viewSelectedOpcode());
    } 
    else if(opCode === null){
      this.disableForm = true;
      this.clearForm();
    }
    if(this.saveClicked)
    {
      this.submitForm();
    }
  }
  loadMileage() {
    this.opCodeStore.dispatch(new fromOpCodeActions.LoadMileages());
    this.mileages$ = this.opCodeStore.select(fromOpCodeSelectors.selectAllMileages);
  }

  cancelForm() {
    if (!!this.opCode) {
      this.viewSelectedOpcode();
    } else {
      this.clearForm();
    }
  }

  get commentsEnabledField() {
    return this.opCodeForm.get('commentsEnabled');
  }

  get commentsRequiredField() {
    return this.opCodeForm.get('commentsRequired');
  }

  get isMisc() {
    return this.opCodeForm.get('isMiscellaneous');
  }


  get opcodeTypeField() {
    return this.opCodeForm.get('opcodeType');
  }

  get opcodeName() {
    return this.opCodeForm.get('opcode');
  }

  get opcodeDescription() {
    return this.opCodeForm.get('description');
  }

  get dmsCode() {
    return this.opCodeForm.get('mapDmsCode') as UntypedFormGroup;
  }

  clearForm() {
    this.isCheckOpcodesLoading$ = this.opCodeStore.select(fromOpCodeSelectors.selectLoading);
    this.isOpcodeExists$ = this.opCodeStore.select(fromOpCodeSelectors.selectIsOpcodeExists);
    this.opCodeForm = this.formBuilder.group({
      labourHours: [null, [Validators.required, Validators.min(0.1)]],
      commentsEnabled: [false],
      commentsRequired: [false],
      isMiscellaneous: [false],
      isTXM: [false],
      opcodeType: [OpcodeType.OTHER],
      dealerCode: [''],
      displayName: ['', [Validators.required]],
      cause: [''],
      dmsDescription: [''],
      description: ['', [Validators.required]],
      isNew: [''],
      isVisible: [''],
      isAspCreated:[false],
      opcode: ['',[Validators.required]],
      recommended: this.formBuilder.group({
        isRecommended: [''],
        isCoveredByToyotaCare: [''],
        isTelematics: [''],
        isSSCRecall: ['']
      }),
      addOnOpcodes: this.formBuilder.group({
        checked: [false],
        description: [''],
        opcodes: this.formBuilder.array([])
      }),
      tags: [''],
      SK: [''],
      PK: [''],
      correction: [''],
      pricing: this.formBuilder.group({
        startsAt: [''],
        range: this.formBuilder.group({
          from: [''],
          to: ['']
        }),
        fixed: [''],
        dms: [''],
        show: [false],
        pricingType: [this.pricingType.FIXED],
        discount: this.formBuilder.group({
          isAvailable: [false],
          validTill: [''],
          price: ['']
        })
      }),
      mileages: [''],
      isCompRentalEligible: [false],
      appointmentsAllowed:[null],
      isInternalCode:[false],
      dmsCode: [''],
      mapDmsCode: this.formBuilder.group({
        opcode: [''],
        opcodeOption: [''],
        isDmsCodeSaved: [false]
      }),
      subComplaints: this.formBuilder.group({
        checked: [false],
        complaintCodes: this.formBuilder.array([])
      }),
    });
    if(this.isAspComplaintCode) {
      this.dmsCode.controls['opcode'].setValidators([Validators.required]);
    }
    // FormGroup Change events
    this.detectFormValueChanges();
  }

  detectFormValueChanges() {
    this.subs.pricingTypeKey = this.opCodeForm.get(this.pricingTypeKey).valueChanges.subscribe(() => {
      this.updatePricingValidations();
    });
    this.subs.pricingShowKey = this.opCodeForm.get(this.pricingShowKey).valueChanges.subscribe(() => {
      this.updatePricingValidations();
      this.updateDiscountValidation();
    });

    this.subs.discountAvailable = this.opCodeForm.get('pricing.discount.isAvailable').valueChanges.subscribe(() => {
      this.updateDiscountValidation();
    });
    this.subs.commentsEnabled = this.opCodeForm.get('commentsEnabled').valueChanges.subscribe(t => {
      if (t) {
        this.commentsRequiredField.enable();
      }
      else {
        this.commentsRequiredField.patchValue(false);
        this.commentsRequiredField.disable();
      }
    });
    this.subs.isMiscellaneous = this.opCodeForm.get('isMiscellaneous').valueChanges.subscribe(t => {
      if (t) {
        this.prevDesc = this.opcodeDescription.value;
        this.opcodeDescription.setValue(this.miscDefaultDescription);
      } else {
        this.opcodeDescription.setValue(this.prevDesc || this.miscDefaultDescription);
      }
      this.opCodeForm.updateValueAndValidity();
    });
    this.subs.opcodeFormChanges = this.opCodeForm.valueChanges.subscribe(()=>{
      this.formEdited.emit(!this.opCodeForm.dirty);
    });
  }

  updatePricingValidations() {
    const isChecked = this.opCodeForm.get(this.pricingShowKey).value;
    if (isChecked) {
      const pricingType = this.opCodeForm.get(this.pricingTypeKey).value;
      this.resetPricingValidations();
      switch (pricingType) {
        case this.pricingType.FIXED:
          this.opCodeForm.get(this.pricingFixedKey).setValidators([Validators.required]);
          this.opCodeForm.get(this.pricingFixedKey).updateValueAndValidity();
          break;
        case this.pricingType.STARTS_AT:
          this.opCodeForm.get(this.pricingStartsAtKey).setValidators([Validators.required]);
          this.opCodeForm.get(this.pricingStartsAtKey).updateValueAndValidity();
          break;
        case this.pricingType.RANGE:
          this.opCodeForm.get(this.pricingRangeFrom).setValidators([Validators.required]);
          this.opCodeForm.get(this.pricingRangeFrom).updateValueAndValidity();
          this.opCodeForm.get(this.pricingRangeTo).setValidators([Validators.required]);
          this.opCodeForm.get(this.pricingRangeTo).updateValueAndValidity();
          break;
        default:
          break;
      }
    } else {
      this.resetPricingValidations();
    }
  }

  resetPricingValidations() {
    this.opCodeForm.get(this.pricingFixedKey).setValidators(null);
    this.opCodeForm.get(this.pricingFixedKey).updateValueAndValidity();
    this.opCodeForm.get(this.pricingStartsAtKey).setValidators(null);
    this.opCodeForm.get(this.pricingStartsAtKey).updateValueAndValidity();
    this.opCodeForm.get(this.pricingRangeFrom).setValidators(null);
    this.opCodeForm.get(this.pricingRangeFrom).updateValueAndValidity();
    this.opCodeForm.get(this.pricingRangeTo).setValidators(null);
    this.opCodeForm.get(this.pricingRangeTo).updateValueAndValidity();
  }

  updateDiscountValidation() {
    const isShowPriceChecked = this.opCodeForm.get(this.pricingShowKey).value;
    const isSpecialChecked = this.opCodeForm.get('pricing.discount.isAvailable').value;
    if (isShowPriceChecked && isSpecialChecked) {
      this.opCodeForm.get(this.discountPrice).setValidators([Validators.required]);
      this.opCodeForm.get(this.discountPrice).updateValueAndValidity();
    } else {
      this.resetDiscountVaidation();
    }
  }

  resetDiscountVaidation() {
    this.opCodeForm.get(this.discountPrice).setValidators(null);
    this.opCodeForm.get(this.discountPrice).updateValueAndValidity();
  }

  viewSelectedOpcode() {
    this.opCodeStore.dispatch(new fromOpCodeActions.LoadOpCode({ opcode: this.opCode }));

    this.isLoading$ = this.opCodeStore.select(fromOpCodeSelectors.selectUpdateOpCodeLoading);
    const opcodeSelector = (!this.isAllOpCodeList) ? fromOpCodeSelectors.selectFromOnlineOpCodes : fromOpCodeSelectors.selectFromAllOpCodes;
    this.opCode$ = this.opCodeStore.select(opcodeSelector);
    this.subs.selectopcode = this.opCode$.pipe(first()).subscribe(opCode => {
      this.isAspComplaintCode = opCode?.isInternalCode || false;
      this.clearForm();
      this.isToyotabrand = opCode.dealerCode.startsWith('6') ? false : true;
      this.opCodeForm.patchValue(opCode);
      this.commentsEnabledField.patchValue(opCode.commentsEnabled ? opCode.commentsEnabled : false);
      this.commentsRequiredField.patchValue(opCode.commentsRequired ? opCode.commentsRequired : false);
      this.opcodeTypeField.patchValue(opCode.opcodeType ? opCode.opcodeType : OpcodeType.OTHER);
      this.opCodeForm.get('mileages').patchValue(this.milageValue);
      this.opCodeForm.get('displayName').patchValue(opCode.displayName ? opCode.displayName : '');
      this.opCodeForm.get('isVisible').patchValue(opCode.isVisible ? opCode.isVisible : false);
      this.opCodeForm.get('pricing.dms').patchValue(opCode.pricing.dms ? opCode.pricing.dms : '');
      this.opCodeForm.get('dmsDescription').patchValue(opCode?.isInternalCode ? '(Not applicable for ASP Complaint Codes)' : opCode.dmsDescription || '(No description available)');
      this.patchOpcodes(opCode.addOnOpcodes?.opcodes);
      this.patchSubComplaintCodes(opCode.subComplaints?.complaintCodes);
      if(this.isAspComplaintCode && opCode.mapDmsCode?.opcode) {
        this.dmsCode.controls['opcodeOption'].patchValue(opCode.mapDmsCode.opcode);
        this.dmsCode.controls['isDmsCodeSaved'].patchValue(true);
      }
      this.opCodeForm.markAllAsTouched();
    });
  }

  updateSwitchValues(val: boolean, property: string, index= undefined) {
    switch (property) {
      case 'isVisible':
        this.opCodeForm.get('isVisible').patchValue(val);
        break;
      case 'recommended.isRecommended':
        this.opCodeForm.get('recommended').get('isRecommended').patchValue(val);
        break;
      case 'recommended.isCoveredByToyotaCare':
        this.opCodeForm.get('recommended').get('isCoveredByToyotaCare').patchValue(val);
        break;
      case 'recommended.isTelematics':
        this.opCodeForm.get('recommended').get('isTelematics').patchValue(val);
        break;
      case 'recommended.isSSCRecall':
        this.opCodeForm.get('recommended').get('isSSCRecall').patchValue(val);
        break;
      case 'isMiscellaneous':
        this.opCodeForm.get('isMiscellaneous').patchValue(val);
        break;
      case 'isCompRentalEligible':
        this.opCodeForm.get('isCompRentalEligible').patchValue(val);
        break;
      case 'isTXM':
        this.opCodeForm.get('isTXM').patchValue(val);
        break;
      default:
        break;
    }
    this.opCodeForm.markAsDirty();
    this.formEdited.emit(!this.opCodeForm.dirty);
  }

  onlyFutureDays = (d: Date | null): boolean => {
    const day = (d || new Date());
    const currentDate = new Date(new Date().setHours(0, 0, 0, 0)).getTime();
    const expiryDateValue = this.opCodeForm.get('pricing.discount.validTill').value;
    const expiryDate = (!expiryDateValue) ? currentDate : new Date(new Date(expiryDateValue).setHours(0, 0, 0, 0)).getTime();
    const date: boolean = (day.getTime() === expiryDate || day.getTime() >= currentDate);
    return date;
  }

  hasError(controlName: string, errorName: string) {
    return this.opCodeForm.get(controlName).hasError(errorName);
  }

  submitForm() {
    if (this.isValidForm()) {

      if(!this.opCodeForm.value?.pricing?.fixed){
        delete this.opCodeForm.value.pricing.fixed;
      }

      if(!this.opCodeForm.value?.pricing?.dms){
        delete this.opCodeForm.value.pricing.dms;
      }

      delete this.opCodeForm.value.mapDmsCode.isDmsCodeSaved;

      delete this.opCodeForm.value.mapDmsCode.opcodeOption;

      this.opCodeForm.value.labourHours = +this.opCodeForm.value.labourHours;

      const opcodeData = { ...this.opCodeForm.value, isNew: false };

      if(opcodeData.addOnOpcodes?.opcodes?.length) {
        opcodeData.addOnOpcodes.opcodes.forEach((opcode, i) => {
          if(opcode?.opcode) {
            const opcodeDetails = opcode;
            opcodeData.addOnOpcodes.opcodes[i] = {opcode: opcodeDetails.opcode, opcodeDetails};
          }
        })
      }

      if(opcodeData.subComplaints?.complaintCodes?.length) {
        opcodeData.subComplaints.complaintCodes.forEach((_item, i) => {
          delete  opcodeData.subComplaints.complaintCodes[i].isDmsCodeSaved;
          delete  opcodeData.subComplaints.complaintCodes[i].dmsCodeOption;
        })
      }

      // set milage as array before submitting
      let mileage = this.opCodeForm.value.mileages;
      if(mileage !== ''){
        mileage = Array.isArray(mileage) ? mileage : [mileage];
      }
      opcodeData.mileages = mileage;

      opcodeData.opcode === this.opCode ? this.opCodeStore.dispatch(new fromOpCodeActions.UpdateOpCode({ opcode: opcodeData,
      isLoadNotificationData: ((this.onlineOpcodesCount === 0 && opcodeData.isVisible) || (this.onlineOpcodesCount === 1 && !opcodeData.isVisible) ) ? true : false})) :
      this.opCodeStore.dispatch(new fromOpCodeActions.UpdateAspOpCodeName({aspServiceCode: this.opCode, opcode: opcodeData}))
      this.opCodeStore.dispatch(new fromOpCodeActions.ChangeIsExistingOpcode());
    }
    this.formEdited.emit(true);
  }

  addMileageValidation(){
    if(this.opCodeForm.value.mileages.length == 0){
      this.opCodeForm.get('mileages').setErrors({ required: this.translate.instant('opcodeServiceManagement.mileageIsRequiredWhenMaintenanceOpcodeIsChoosen') });
    }
  }

  removeMileageValidation(){
    this.opCodeForm.get('mileages').setErrors(null, { emitEvent: false });
  }

  get addOnOpcodes(){
    return (this.opCodeForm.controls.addOnOpcodes as UntypedFormGroup).controls.opcodes as UntypedFormArray;
  }

  get subComplaintCodes(){
    return (this.opCodeForm.controls.subComplaints as UntypedFormGroup).controls.complaintCodes as UntypedFormArray;
  }

  get issubComplaintCodeEnabled(){
    return (this.opCodeForm.controls.subComplaints as UntypedFormGroup).controls.checked;
  }

  patchOpcodes(opcodes){
    this.addOnOpcodes.clear();
    if(opcodes && opcodes.length){
      opcodes.forEach((opcode, i) => {
        if(!this.addOnOpcodes.controls[i]){
          this.addOnOpcodes.push(new UntypedFormGroup({
            opcode: new UntypedFormControl(opcode.opcode),
            displayName: new UntypedFormControl(opcode.opcodeDetails?.displayName || opcode.opcodeDetails.dmsDescription),
            labourhours: new UntypedFormControl(opcode.opcodeDetails.labourHours)
          }));
        }else{
          this.addOnOpcodes.controls[i].patchValue(new UntypedFormGroup({
            opcode: new UntypedFormControl(opcode.opcode),
            displayName: new UntypedFormControl(opcode.opcodeDetails?.displayName || opcode.opcodeDetails.dmsDescription),
            labourhours: new UntypedFormControl(opcode.opcodeDetails.labourHours)
          }));
        }
      })
    }

  }

  setSelectedOpcode(opcode){
    this.selectedOpcode = opcode.target.value;
  }

  get isValidAddOnOpcode(){
    return this.addOnOpcodes.value.findIndex(x => x.opcode === this.selectedOpcode) == -1
    && this.opCodeForm.value.opcode !== this.selectedOpcode
  }

  onAddOpcode() {
    if(this.isValidAddOnOpcode){
        const opcode = this.allDmsOpcodes.find(x=> x.opcode.toLowerCase() === this.selectedOpcode.toLowerCase());
        if(opcode){
          this.addOnOpcodes.push(new UntypedFormGroup({
            opcode: new UntypedFormControl(this.selectedOpcode),
            displayName: new UntypedFormControl(opcode.displayName || opcode.dmsDescription),
            labourhours: new UntypedFormControl(opcode.labourHours)
          }));
          this.selectedOpcode = "";
        }
    }else{
      this.alertService.showAlert('otherTexts.opcodeAlreadyExistsOrYourTryingToAddTheSelectedOpcode');
    }
  }

  onDeleteOpcode(index: number) {
    this.addOnOpcodes.removeAt(index);
  }

  getOpcodeDetails(opcode){
    return this.opCodeStore.select(fromOpCodeSelectors.selectOpcodeById, {id: opcode});
  }

showPricingValueChange(){
  const pricingShow = this.opCodeForm.get(this.pricingShowKey).valueChanges.subscribe(()=>{
     this.updatePricingValidations();
     this.updateDiscountValidation();
     pricingShow.unsubscribe();
   })
 }
 pricingTypeChange(){
   const pricingShow = this.opCodeForm.get(this.pricingTypeKey).valueChanges.subscribe(()=>{
     this.updatePricingValidations();
     pricingShow.unsubscribe();
   })
 }

 hasTouched(controlName: string){
  return this.opCodeForm.get(controlName).touched;
}
deleteAspService($event) {
    $event.stopPropagation();
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      height: '220px',
      data: {
        popupTitle: 'Delete ASP Service Code',
        popupBodyText: 'Are you sure do you want to delete this ASP Service Code?'
      },
      panelClass: 'asp-modal-container'
    });
    this.subs.dialogRef = dialogRef.afterClosed().subscribe((result) => {
      if (result.action === 'confirm') {
        this.opCodeStore.dispatch(new fromOpCodeActions.DeleteAspServiceCode({aspServiceCode: this.opCode}))
      }
    });
  }

  hasChildItemsError(errorName: string, i: number) {
    this.subComplaintCodes.controls[i].hasError(errorName);
  }

  setChildItemsValidator(): ValidatorFn {
    return (item: UntypedFormGroup): ValidationErrors => {
      if (this.issubComplaintCodeEnabled.value) {
        this.validatedFields.forEach(fields => {
          if (!item.controls[fields].value) {
            item.controls[fields].setErrors({ required: `${fields} is required` });
          }
        });
      }
      return null;
    };
  }

  removeChildItemsValidator(index: number) {
    const item = this.subComplaintCodes.controls[index] as UntypedFormGroup;
    this.validatedFields.forEach(field => item.controls[field].setErrors(null, { emitEvent: false }));
  }

  onSubComplaintCodesChecked() {
    if (this.subComplaintCodes.value.length) {
      this.subComplaintCodes.controls.forEach((a, i) => {
        if (this.issubComplaintCodeEnabled.value) {
          a.setValidators(this.setChildItemsValidator());
          a.updateValueAndValidity();
        } else {
          this.removeChildItemsValidator(i);
        }
      });
    }
  }

  onDeleteChildItem(index: number) {
    this.subComplaintCodes.removeAt(index);
  }

  onAddSubComplaintCode() {
    this.subComplaintCodes.push(new UntypedFormGroup({
      opcode: new UntypedFormControl(''),
      online: new UntypedFormControl(false),
      dmsCode: new UntypedFormControl(''),
      dmsCodeOption: new UntypedFormControl(''),
      isDmsCodeSaved: new UntypedFormControl(false),
      displayName: new UntypedFormControl(this.addonSubCode.value),
      userComments: new UntypedFormControl(false),
      commentsRequired: new UntypedFormControl(false)
    }));
    const index = this.subComplaintCodes.length - 1;
    (this.subComplaintCodes.controls[index] as UntypedFormGroup).setValidators(this.setChildItemsValidator());
    (this.subComplaintCodes.controls[index] as UntypedFormGroup).markAllAsTouched();
    this.addonSubCode.patchValue('');
    this.allDmsOpcodesSubOptions[index]= [...this.allDmsOpcodes.slice()];
  }

  getFormGroupForSubCode(item) {
    return new UntypedFormGroup({
      opcode: new UntypedFormControl(item.opcode),
      displayName: new UntypedFormControl(item.displayName),
      dmsCode:  new UntypedFormControl(item?.dmsCode ? item?.dmsCode : ''),
      dmsCodeOption: new UntypedFormControl(item?.dmsCode ? item.dmsCode: ''),
      isDmsCodeSaved: new UntypedFormControl(!!item?.dmsCode),
      userComments: new UntypedFormControl(item.userComments),
      online: new UntypedFormControl(item.online),
      commentsRequired: new UntypedFormControl(item.commentsRequired),
    });
  }

  patchSubComplaintCodes(complaintCodes) {
    this.subComplaintCodes.clear();
    if (complaintCodes && complaintCodes.length) {
      complaintCodes.forEach((item, i) => {
        if (!this.subComplaintCodes.controls[i]) {
          this.subComplaintCodes.push(this.getFormGroupForSubCode(item));
        } else {
          this.subComplaintCodes.controls[i].patchValue(this.getFormGroupForSubCode(item));
        }
        this.subComplaintCodes.controls[i].setValidators(this.setChildItemsValidator());
        this.allDmsOpcodesSubOptions[i] = [...this.allDmsOpcodes.slice()];
      })
    }
  }

  filter(value): void {
    const filterValue = value.toLowerCase();
    this.filteredOptions = this.allDmsOpcodes.filter(o => o.opcode.toLowerCase().includes(filterValue)
     || (o.displayName ? o.displayName : o.dmsDescription).toLowerCase().includes(filterValue));
  }

  mapDmsOpcode() {
    if(this.dmsCode.value.opcodeOption) {
      this.dmsCode.controls['opcode'].patchValue(this.dmsCode.value.opcodeOption);
      this.dmsCode.controls['isDmsCodeSaved'].patchValue(true);
    }
  }

  onEditDmsOpcode() {
    this.dmsCode.controls['isDmsCodeSaved'].patchValue(false);
  }

  onDeleteDmsOpcode() {
    this.dmsCode.controls['opcode'].patchValue('');
    this.dmsCode.controls['opcodeOption'].patchValue('');
    this.dmsCode.controls['isDmsCodeSaved'].patchValue(false);
    this.filteredOptions = this.allDmsOpcodes.slice();
  }

  filterSub(value, i: number): void {
    const filterValue = value.toLowerCase();
    this.allDmsOpcodesSubOptions[i] = this.allDmsOpcodes.filter(o => o.opcode.toLowerCase().includes(filterValue)
     || (o.displayName ? o.displayName : o.dmsDescription).toLowerCase().includes(filterValue));
  }

  mapSubDmsOpcode(i: number) {
    if(this.subComplaintCodes.controls[i].value.dmsCodeOption) {
      (this.subComplaintCodes.controls[i] as UntypedFormGroup).controls['dmsCode'].patchValue(this.subComplaintCodes.controls[i].value.dmsCodeOption);
      (this.subComplaintCodes.controls[i] as UntypedFormGroup).controls['isDmsCodeSaved'].patchValue(true);
    }
  }

  onDeleteSubDmsOpcode(i: number) {
    (this.subComplaintCodes.controls[i] as UntypedFormGroup).controls['dmsCode'].patchValue('');
    (this.subComplaintCodes.controls[i] as UntypedFormGroup).controls['dmsCode'].patchValue('');
    (this.subComplaintCodes.controls[i] as UntypedFormGroup).controls['isDmsCodeSaved'].patchValue(false);
    this.allDmsOpcodesSubOptions[i] = this.allDmsOpcodes.slice();
  }

  onEditSubDmsOpcode(i: number) {
    (this.subComplaintCodes.controls[i] as UntypedFormGroup).controls['isDmsCodeSaved'].patchValue(false);
  }

isValidForm() {
  return this.opCodeForm.valid && (this.isAspComplaintCode ? this.dmsCode.value.opcode && this.dmsCode.value.isDmsCodeSaved : true);
}
  
  ngOnDestroy() {
    unsubscribeSubscriptions(this.subs);
  }
}
